import React, { useState } from 'react';
import { Tag, Heading, Lead, PageHero, Section, BtnPrimary } from '../components/UI';
import styles from './Training.module.css';

const COURSES = [
  {
    n: '01', t: 'Java Full Stack',
    desc: 'Master enterprise-grade application development with Core Java, Spring Boot, Hibernate, REST APIs, and React frontend integration.',
    topics: ['Core Java & OOPs', 'Spring Boot & MVC', 'Hibernate & JPA', 'React.js Frontend', 'REST APIs', 'MySQL & MongoDB'],
    duration: '4 Months', mode: 'Online',
  },
  {
    n: '02', t: '.Net Full Stack',
    desc: 'Build powerful applications with C#, ASP.NET Core, Entity Framework, SQL Server, and Angular or React for the frontend.',
    topics: ['C# & .NET Core', 'ASP.NET MVC', 'Entity Framework', 'Angular / React', 'SQL Server', 'Azure Basics'],
    duration: '4 Months', mode: 'Online',
  },
  {
    n: '03', t: 'Python Full Stack',
    desc: 'From Django backend to React frontend — develop full-stack web applications with Python, with hands-on project work throughout.',
    topics: ['Python Fundamentals', 'Django & Flask', 'REST APIs', 'React Frontend', 'PostgreSQL', 'Deployment & DevOps'],
    duration: '3.5 Months', mode: 'Online',
  },
  {
    n: '04', t: 'AI & Machine Learning',
    desc: 'Dive into machine learning, deep learning, NLP, computer vision, and AI application development with Python and TensorFlow.',
    topics: ['Python for AI', 'ML Algorithms', 'Deep Learning', 'NLP & Computer Vision', 'TensorFlow & PyTorch', 'Model Deployment'],
    duration: '4 Months', mode: 'Online',
  },
  {
    n: '05', t: 'Salesforce',
    desc: 'Comprehensive Salesforce training covering Admin, Developer, and Architect tracks — prepare for globally recognized certifications.',
    topics: ['Salesforce Admin', 'Apex & Visualforce', 'Lightning Web Components', 'Salesforce DX', 'Integration', 'Certification Prep'],
    duration: '3 Months', mode: 'Online',
  },
  {
    n: '06', t: 'Power BI',
    desc: 'Transform raw data into powerful visual insights with Power BI Desktop, DAX, Power Query, and enterprise reporting skills.',
    topics: ['Power BI Desktop', 'DAX Formulas', 'Power Query', 'Data Modeling', 'Dashboard Design', 'Power BI Service'],
    duration: '2 Months', mode: 'Online',
  },
  {
    n: '07', t: 'QA Automation',
    desc: 'Master modern testing frameworks including Selenium, Cypress, and TestNG, with CI/CD integration and API testing skills.',
    topics: ['Manual Testing', 'Selenium WebDriver', 'TestNG & JUnit', 'Cypress.io', 'API Testing', 'CI/CD & Jenkins'],
    duration: '3 Months', mode: 'Online',
  },
];

const FEATURES = [
  { icon: '🎓', t: 'Certified Instructors',    d: 'All courses taught by certified and experienced industry professionals.' },
  { icon: '💻', t: 'Learn from Anywhere',      d: 'Fully online — everything done from your computer\'s desktop, on your schedule.' },
  { icon: '📚', t: 'Rich Course Content',       d: 'Exhaustive explanations, detailed coverage, and practical project-based learning.' },
  { icon: '💰', t: 'Competitive Pricing',       d: 'High-quality training at affordable rates with flexible payment options.' },
  { icon: '🏆', t: 'Industry Certification',   d: 'Prepare for globally recognized certifications that boost your career.' },
  { icon: '🤝', t: 'Career Support',            d: 'Job placement assistance and interview preparation for course graduates.' },
];

export default function Training() {
  const [selected, setSelected] = useState(0);
  const course = COURSES[selected];

  return (
    <>
      <PageHero
        tag="IT Training"
        title="Learn from Certified Industry Experts"
        subtitle="Metawave Solutions offers high-quality, state-of-the-art IT and business-related e-learning training courses — competitive prices, rich content, and learner-friendly delivery."
      />

      {/* ── Features ───────────────────────────────────────────── */}
      <Section variant="paper">
        <Tag>Why Train With Us</Tag>
        <Heading>What Makes Our Training Different</Heading>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) => (
            <div key={i} className={styles.featureCard}>
              <div className={styles.featureIcon}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.t}</h3>
              <p className={styles.featureDesc}>{f.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Course Explorer ─────────────────────────────────────── */}
      <Section variant="white">
        <Tag>Our Curriculum</Tag>
        <Heading>Courses Offered</Heading>
        <Lead>Seven industry-aligned programs designed to launch or advance your career in tech.</Lead>
        <div className={styles.explorer}>
          {/* Sidebar */}
          <div className={styles.courseList}>
            {COURSES.map((c, i) => (
              <button
                key={i}
                className={`${styles.courseBtn} ${selected === i ? styles.courseBtnActive : ''}`}
                onClick={() => setSelected(i)}
              >
                <span className={styles.courseBtnNum}>{c.n}</span>
                <span className={styles.courseBtnTitle}>{c.t}</span>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className={styles.courseDetail} key={selected}>
            <div className={styles.detailMeta}>
              <span className={styles.metaBadge}>⏱ {course.duration}</span>
              <span className={styles.metaBadge}>🖥 {course.mode}</span>
            </div>
            <h2 className={styles.detailTitle}>{course.t}</h2>
            <p className={styles.detailDesc}>{course.desc}</p>

            <div className={styles.topicsSection}>
              <h4 className={styles.topicsHeading}>Topics Covered</h4>
              <div className={styles.topicsGrid}>
                {course.topics.map((topic, i) => (
                  <div key={i} className={styles.topicChip}>
                    <span className={styles.topicDot} />
                    {topic}
                  </div>
                ))}
              </div>
            </div>

            <BtnPrimary to="/contact-us" style={{ marginTop: '2rem' }}>
              Enroll in {course.t} →
            </BtnPrimary>
          </div>
        </div>
      </Section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      {/* <Section variant="ink" style={{ textAlign: 'center' }}>
        <Tag light>Get Started Today</Tag>
        <Heading light>Ready to Advance Your Career?</Heading>
        <Lead light style={{ margin: '0 auto 2rem' }}>
          Join thousands of professionals who have upskilled with Metawave Solutions training programs.
        </Lead>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <BtnPrimary to="/contact">Enroll Now</BtnPrimary>
        </div>
      </Section> */}
    </>
  );
}
